import { cn } from "@faire/prototype-playground/lib/utils";
import {
  PlaygroundLocalMsg,
  usePlaygroundLocalMsg,
} from "@faire/prototype-playground/platform/localization";
import { Button } from "@faire/slate/components/button";
import { Trash } from "@phosphor-icons/react";

import { RetailerMobileNav } from "./RetailerMobileNav";
import {
  RETURN_REQUEST_PRODUCTS,
  type ReturnRequestProduct,
} from "./returnRequestData";

type SelectProductsScreenProps = {
  selectedIds: ReadonlySet<string>;
  onSelectedIdsChange: (nextIds: Set<string>) => void;
  onContinue: () => void;
};

const SelectProductCard = ({
  product,
  selected,
  onSelect,
  onRemove,
}: {
  product: ReturnRequestProduct;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) => {
  const removeLabel = usePlaygroundLocalMsg("Remove {name}", {
    name: product.name,
  });

  return (
    <div
      className={cn(
        "flex w-full gap-(--slate-spacing-element-medium) rounded-[4px] border p-(--slate-spacing-element-large) text-left",
        selected
          ? "bg-fs-surface-tertiary border-fs-action-border-active"
          : "bg-fs-action-surface-inverse border-fs-action-border-subdued cursor-pointer"
      )}
      role={selected ? undefined : "button"}
      tabIndex={selected ? undefined : 0}
      onClick={selected ? undefined : onSelect}
      onKeyDown={
        selected
          ? undefined
          : (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect();
              }
            }
      }
    >
      <div className="bg-fs-surface-tertiary size-[72px] shrink-0 overflow-hidden rounded-[2px]">
        <img
          src={product.imageUrl}
          alt=""
          className="size-full object-cover"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        {selected ? (
          <div className="flex items-start justify-between gap-(--slate-dimensions-2)">
            <span className="type-fs-paragraph-medium text-fs-message-text-critical">
              {product.issueLabel}
            </span>
            <button
              type="button"
              aria-label={removeLabel}
              className="text-fs-text-primary flex size-4 shrink-0 items-center justify-center"
              onClick={(event) => {
                event.stopPropagation();
                onRemove();
              }}
            >
              <Trash size={16} weight="regular" />
            </button>
          </div>
        ) : null}
        <span className="type-fs-paragraph-medium text-fs-text-primary">
          {product.name}
        </span>
        <span className="type-fs-paragraph text-fs-text-primary">
          {product.color}
        </span>
        <span className="type-fs-paragraph text-fs-text-primary">
          {product.size}
        </span>
      </div>
    </div>
  );
};

export const SelectProductsScreen = ({
  selectedIds,
  onSelectedIdsChange,
  onContinue,
}: SelectProductsScreenProps) => {
  const continueLabel = usePlaygroundLocalMsg("Continue");

  const handleSelect = (productId: string) => {
    const nextIds = new Set(selectedIds);
    nextIds.add(productId);
    onSelectedIdsChange(nextIds);
  };

  const handleRemove = (productId: string) => {
    const nextIds = new Set(selectedIds);
    nextIds.delete(productId);
    onSelectedIdsChange(nextIds);
  };

  return (
    <div className="bg-fs-surface-primary flex min-h-full flex-col">
      <RetailerMobileNav progressFraction={0.25} />
      <div className="flex flex-1 flex-col gap-(--slate-spacing-section-large) px-(--slate-spacing-section-medium) pt-(--slate-spacing-section-medium) pb-(--slate-spacing-section-large)">
        <div className="flex flex-col gap-(--slate-dimensions-1)">
          <h1 className="type-fs-page-heading text-fs-text-primary">
            <PlaygroundLocalMsg defaultMessage="Which products had an issue?" />
          </h1>
          <p className="type-fs-paragraph text-fs-text-primary">
            <PlaygroundLocalMsg defaultMessage="Select any other affected products" />
          </p>
        </div>
        <div className="flex flex-col gap-(--slate-dimensions-2)">
          {RETURN_REQUEST_PRODUCTS.map((product) => (
            <SelectProductCard
              key={product.id}
              product={product}
              selected={selectedIds.has(product.id)}
              onSelect={() => handleSelect(product.id)}
              onRemove={() => handleRemove(product.id)}
            />
          ))}
        </div>
      </div>
      <div className="bg-fs-surface-primary sticky bottom-0 px-(--slate-spacing-section-medium) pt-(--slate-dimensions-2) pb-(--slate-spacing-section-medium)">
        <Button
          variant="primary"
          fullWidth
          disabled={selectedIds.size === 0}
          onClick={onContinue}
        >
          {continueLabel}
        </Button>
      </div>
    </div>
  );
};
